import { Link } from 'react-router-dom';
import { Network, Briefcase, Target, Users } from 'lucide-react';
import Typewriter from 'typewriter-effect';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { trackEvent } from '../lib/analytics';

// Add this CSS at the top of your file for the zoom animation
const imageStyles = `
  transition: transform 0.3s ease-in-out;
  cursor: pointer;
`;

const zoomedImageStyles = `
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
`;

const structuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Network Relationship Manager",
  "applicationCategory": "BusinessApplication",
  "offers": {
    "@type": "Offer",
    "price": "15",
    "priceCurrency": "USD",
    "priceValidUntil": "2024-12-31",
  },
  "description": "Professional network management tool for job seekers, sales professionals, and network builders.",
};

export function LandingPage() {
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  // Add click handlers
  const handleImageClick = (imageSrc: string) => {
    setZoomedImage(imageSrc);
  };

  const handleZoomedImageClick = () => {
    setZoomedImage(null);
  };

  const scrollToPricing = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleStartTrial = () => {
    trackEvent('trial_started')
  }

  const handleEnterpriseContact = () => {
    trackEvent('enterprise_contact')
  }

  return (
    <>
      <Helmet>
        <title>Network Relationship Manager (NRM) - Manage Your Professional Network</title>
        <meta name="description" content="NRM helps you nurture relationships, track opportunities, and stay top-of-mind with the people who matter most in your professional network." />
        
        {/* Open Graph / Social Media Meta Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Network Relationship Manager (NRM)" />
        <meta property="og:description" content="Build stronger professional relationships and never miss a follow-up opportunity." />
        <meta property="og:image" content="https://nrm-dev.neosavvy.com/images/dashboard-preview.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://nrm-dev.neosavvy.com" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Network Relationship Manager (NRM)" />
        <meta name="twitter:description" content="Build stronger professional relationships and never miss a follow-up opportunity." />
        <meta name="twitter:image" content="https://nrm-dev.neosavvy.com/images/dashboard-preview.png" />
        <meta name="twitter:domain" content="nrm-dev.neosavvy.com" />
        
        {/* Keywords and other SEO meta tags */}
        <meta name="keywords" content="network management, relationship management, professional networking, CRM, contact management, job search, sales networking" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://nrm-dev.neosavvy.com" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* Additional tags for better iMessage preview */}
        <meta property="og:site_name" content="Network Relationship Manager" />
        <meta property="og:image:secure_url" content="https://nrm-dev.neosavvy.com/images/dashboard-preview.png" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:alt" content="NRM Dashboard showing contact management interface" />
        
        {/* Apple-specific meta tags */}
        <meta name="apple-mobile-web-app-title" content="Network Relationship Manager" />
        <link rel="apple-touch-icon" href="https://nrm-dev.neosavvy.com/images/dashboard-preview.png" />
      </Helmet>

      <div className="min-h-screen bg-dark-900">
        <header>
          <nav className="bg-dark-800 border-b border-dark-700">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-16 items-center">
                <div className="flex items-center">
                  <Network className="h-8 w-8 text-primary-500" />
                  <span className="ml-2 text-xl font-bold text-white">NRM</span>
                </div>
                <Link
                  to="/login"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600"
                >
                  Sign In
                </Link>
              </div>
            </div>
          </nav>
        </header>

        <main>
          <section aria-label="Hero" className="relative overflow-hidden py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl">
                  <span className="block">Never Let Your Network</span>
                  <span className="block text-primary-500">Go Cold Again</span>
                </h1>
                <p className="mt-3 max-w-md mx-auto text-base text-gray-300 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
                  Your network is your most valuable asset. Network Relationship Manager (NRM) helps you nurture relationships, track opportunities, and stay top-of-mind with the people who matter most.
                </p>

                {/* Value Props for Each Persona */}
                <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-3">
                  {/* Job Seekers */}
                  <div className="p-6 rounded-lg bg-dark-800">
                    <div className="flex flex-col items-center">
                      <Briefcase className="h-8 w-8 text-primary-500 mb-4" />
                      <h3 className="text-lg font-semibold text-white mb-2">Job Seekers</h3>
                      <p className="text-gray-300 text-center">
                        Keep warm relationships with past colleagues and hiring managers for your next career move
                      </p>
                    </div>
                  </div>

                  {/* Sales People */}
                  <div className="p-6 rounded-lg bg-dark-800">
                    <div className="flex flex-col items-center">
                      <Target className="h-8 w-8 text-primary-500 mb-4" />
                      <h3 className="text-lg font-semibold text-white mb-2">Sales Professionals</h3>
                      <p className="text-gray-300 text-center">
                        Turn cold outreach into warm introductions through your existing network
                      </p>
                    </div>
                  </div>

                  {/* Network Builders */}
                  <div className="p-6 rounded-lg bg-dark-800">
                    <div className="flex flex-col items-center">
                      <Users className="h-8 w-8 text-primary-500 mb-4" />
                      <h3 className="text-lg font-semibold text-white mb-2">Network Builders</h3>
                      <p className="text-gray-300 text-center">
                        Never miss a follow-up with important contacts between career moves
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-10 max-w-md mx-auto sm:flex sm:justify-center sm:space-x-4 md:mt-12">
                  <div className="rounded-md shadow">
                    <Link
                      to="/register"
                      onClick={handleStartTrial}
                      className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600 md:py-4 md:text-lg md:px-10"
                    >
                      Start Free Trial
                    </Link>
                  </div>
                  <div className="mt-3 sm:mt-0 rounded-md shadow">
                    <a
                      href="#pricing"
                      onClick={scrollToPricing}
                      className="w-full flex items-center justify-center px-8 py-3 border border-dark-700 text-base font-medium rounded-md text-white bg-dark-800 hover:bg-dark-700 md:py-4 md:text-lg md:px-10"
                    >
                      View Pricing
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section aria-label="Features" className="py-32 bg-dark-900 space-y-40">
            <article>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-20">
                  <div className="order-2 lg:order-1 lg:w-1/2 space-y-8 mt-8 lg:mt-0">
                    <h2 className="text-3xl font-extrabold text-white">
                      Smart Dashboards for Intentional Networking
                    </h2>
                    <p className="mt-4 text-lg text-gray-300">
                      Break the ice with contacts you haven't spoken to in a while, track your follow-ups, and never miss an opportunity to nurture important relationships.
                    </p>
                    <ul className="mt-8 space-y-4 text-gray-300">
                      <li className="flex items-center">
                        <span>• Break the Ice reminders for dormant relationships</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Follow-up tracking and scheduling</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Action-oriented networking tasks</span>
                      </li>
                    </ul>
                  </div>
                  <div className="order-1 lg:order-2 lg:w-1/2">
                    <div className="bg-dark-800 rounded-lg shadow-xl p-4">
                      <img
                        className="w-full rounded"
                        src="/images/dashboard-preview.png"
                        alt="NRM Dashboard Interface"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </article>

            {/* Kanban Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-20">
                <div className="order-1 lg:w-1/2">
                  <div className="bg-dark-800 rounded-lg shadow-xl p-4">
                    <img
                      className="w-full rounded"
                      src="/images/kanban-preview.png"
                      alt="Kanban Board Interface"
                    />
                  </div>
                </div>
                <div className="order-2 lg:w-1/2 space-y-8 mt-8 lg:mt-0">
                  <h2 className="text-3xl font-extrabold text-white">
                    Track Every Opportunity with Kanban
                  </h2>
                  <p className="mt-4 text-lg text-gray-300">
                    Visualize and manage your opportunities with a flexible Kanban board. Move contacts through your pipeline with simple drag and drop.
                  </p>
                  <ul className="mt-8 space-y-4 text-gray-300">
                    <li className="flex items-center">
                      <span>• Customizable opportunity stages</span>
                    </li>
                    <li className="flex items-center">
                      <span>• Visual pipeline management</span>
                    </li>
                    <li className="flex items-center">
                      <span>• Drag-and-drop interface</span>
                    </li>
                    <li className="flex items-center">
                      <span>• At-a-glance contact details</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Focus Tools Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-32">
                <div className="order-2 lg:order-1 lg:w-1/2 space-y-8 mt-8 lg:mt-0">
                  <h2 className="text-3xl font-extrabold text-white">
                    Focus Tools for Targeted Networking
                  </h2>
                  <p className="mt-4 text-lg text-gray-300">
                    Purpose-built tools to help you move your network towards specific opportunities and goals.
                  </p>
                  <ul className="mt-8 space-y-4 text-gray-300">
                    <li className="flex items-center">
                      <span>• Guided networking workflows</span>
                    </li>
                    <li className="flex items-center">
                      <span>• Contact prioritization</span>
                    </li>
                    <li className="flex items-center">
                      <span>• Opportunity-specific action plans</span>
                    </li>
                  </ul>
                </div>
                <div className="order-1 lg:order-2 lg:w-1/2">
                  <div className="bg-dark-800 rounded-lg shadow-xl p-4">
                    <img
                      className="w-full rounded"
                      src="/images/focus-tools-preview.png"
                      alt="Focus Tools Interface"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Pipeline Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-20">
                <div className="order-1 lg:w-1/2">
                  <div className="bg-dark-800 rounded-lg shadow-xl p-4">
                    <img
                      className="w-full rounded"
                      src="/images/pipeline-preview.png"
                      alt="Opportunity Pipeline Interface"
                    />
                  </div>
                </div>
                <div className="order-2 lg:w-1/2 space-y-8 mt-8 lg:mt-0">
                  <h2 className="text-3xl font-extrabold text-white">
                    Convert Your Network into Opportunities
                  </h2>
                  <p className="mt-4 text-lg text-gray-300">
                    Track your opportunity pipeline whether you're job hunting, freelancing, or in sales.
                  </p>
                  <ul className="mt-8 space-y-4 text-gray-300">
                    <li className="flex items-center">
                      <span>• Pipeline visualization</span>
                    </li>
                    <li className="flex items-center">
                      <span>• Conversion tracking</span>
                    </li>
                    <li className="flex items-center">
                      <span>• ROI measurement for networking efforts</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact Management Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-32">
                <div className="order-2 lg:order-1 lg:w-1/2 space-y-8 mt-8 lg:mt-0">
                  <h2 className="text-3xl font-extrabold text-white">
                    Keep Your Network Details Fresh
                  </h2>
                  <p className="mt-4 text-lg text-gray-300">
                    Never forget important details about your contacts. Automatically sync communications and take notes to maintain context for every relationship.
                  </p>
                  <ul className="mt-8 space-y-4 text-gray-300">
                    <li className="flex items-center">
                      <span>• Capture key notes and follow-up reminders</span>
                    </li>
                    <li className="flex items-center">
                      <span>• Gmail integration for communication history</span>
                    </li>
                    <li className="flex items-center">
                      <span>• Timeline view of all interactions</span>
                    </li>
                    <li className="flex items-center">
                      <span>• Quick actions with keyboard shortcuts</span>
                    </li>
                  </ul>
                </div>
                <div className="order-1 lg:order-2 lg:w-1/2">
                  <div className="bg-dark-800 rounded-lg shadow-xl p-4">
                    <img
                      className="w-full rounded"
                      src="/images/contact-details.png"
                      alt="Contact Details and Timeline Interface"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section aria-label="Pricing" id="pricing" className="bg-dark-800 py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
                  Simple, Transparent Pricing
                </h2>
              </div>
              <div className="mt-12 space-y-4 sm:mt-16 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-8 lg:max-w-6xl lg:mx-auto">
                {/* Free Tier */}
                <div className="border border-dark-700 rounded-lg shadow-sm p-8 bg-dark-900 flex flex-col">
                  <div className="flex-grow">
                    <h3 className="text-2xl font-semibold text-white">Free Trial</h3>
                    <p className="mt-4 text-lg text-gray-300">Perfect for trying out NRM</p>
                    <ul className="mt-8 space-y-4 text-base text-gray-300 mb-12">
                      <li className="flex items-center">
                        <span>• Up to 30 Contacts</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Up to 2 Opportunities</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Single Gmail Integration</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Full Keyboard Shortcuts</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Focus Flow Controls</span>
                      </li>
                    </ul>
                  </div>
                  <div className="mt-auto">
                    <p className="mb-8">
                      <span className="text-5xl font-extrabold text-white">$0</span>
                      <span className="text-lg text-gray-300">/14 days</span>
                    </p>
                    <Link
                      to="/register"
                      onClick={handleStartTrial}
                      className="block w-full bg-primary-500 text-white rounded-md py-3 text-base font-semibold text-center hover:bg-primary-600"
                    >
                      Start Trial
                    </Link>
                  </div>
                </div>

                {/* Pro Tier */}
                <div className="border border-primary-500 rounded-lg shadow-sm p-8 bg-dark-900 relative flex flex-col">
                  <div className="flex-grow">
                    <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-500 text-white">
                        RECOMMENDED
                      </span>
                    </div>
                    <h3 className="text-2xl font-semibold text-white">Professional</h3>
                    <p className="mt-4 text-lg text-gray-300">Everything you need to succeed</p>
                    <ul className="mt-8 space-y-4 text-base text-gray-300">
                      <li className="flex items-center">
                        <span>• Unlimited Contacts</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Unlimited Opportunities</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Multiple Gmail Integrations</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Full Keyboard Shortcuts</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Focus Flow Controls</span>
                      </li>
                    </ul>
                  </div>
                  <div className="mt-auto">
                    <p className="mb-8">
                      <span className="text-5xl font-extrabold text-white">$15</span>
                      <span className="text-lg text-gray-300">/month</span>
                    </p>
                    <Link
                      to="/register"
                      className="block w-full bg-primary-500 text-white rounded-md py-3 text-base font-semibold text-center hover:bg-primary-600"
                    >
                      Get Started
                    </Link>
                  </div>
                </div>

                {/* Enterprise Tier */}
                <div className="border border-dark-700 rounded-lg shadow-sm p-8 bg-dark-900 flex flex-col">
                  <div className="flex-grow">
                    <h3 className="text-2xl font-semibold text-white">Enterprise</h3>
                    <p className="mt-4 text-lg text-gray-300">Your own private NRM instance</p>
                    <ul className="mt-8 space-y-4 text-base text-gray-300">
                      <li className="flex items-center">
                        <span>• Self-hosted on your domain</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Integrated with your Google Workspace</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Complete data sovereignty</span>
                      </li>
                      <li className="flex items-center">
                        <span>• Custom support SLA</span>
                      </li>
                    </ul>
                  </div>
                  <div className="mt-auto">
                    <p className="mb-8">
                      <span className="text-5xl font-extrabold text-white">Custom Pricing</span>
                    </p>
                    <a
                      href="mailto:enterprise@nrm.com"
                      onClick={handleEnterpriseContact}
                      className="block w-full bg-dark-700 text-white rounded-md py-2 text-sm font-semibold text-center hover:bg-dark-600"
                    >
                      Contact Enterprise Sales
                    </a>
                  </div>
                </div>
              </div>
              {/* Add support message */}
              <div className="mt-8 text-center">
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-dark-700 border border-dark-600">
                  <span className="text-sm text-gray-300">Community and Email Support Included</span>
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer className="bg-dark-800 border-t border-dark-700">
          <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
            <nav className="flex flex-wrap justify-center space-x-6">
              <Link to="/terms" className="text-gray-300 hover:text-white">
                Terms of Service
              </Link>
              <Link to="/privacy_policy" className="text-gray-300 hover:text-white">
                Privacy Policy
              </Link>
              <a href="mailto:support@nrm.com" className="text-gray-300 hover:text-white">
                Contact
              </a>
            </nav>
            <p className="mt-8 text-center text-gray-400">
              &copy; {new Date().getFullYear()} NRM. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
} 